import React, { useEffect, useState } from "react";
import { Button, Card, CardBody, Col, Row, Form } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { SectionService } from "./service";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import { Editor } from "@tinymce/tinymce-react";
import { useTranslation } from "react-i18next";
import FormErrorSummary from "../../shared/components/FormErrorSummary";

const schema = yup.object().shape({
    title: yup.string().required('formErrors.titleRequired'),
    content: yup.string().required('formErrors.contentRequired')
});

interface SectionState {
    title: string;
    content: string;
}

const SectionForm: React.FC = () => {
    const { t } = useTranslation(); // Initialize translation hook
    const { id } = useParams<{ id: string }>();
    const isEdit = Boolean(id);
    const [sectionData, setSectionData] = useState<SectionState | null>(null);
    const navigate = useNavigate();
    const { control, handleSubmit, reset, formState: { errors } } = useForm<SectionState>({
        resolver: yupResolver(schema),
        defaultValues: {
            title: "",
            content: ""
        }
    });

    useEffect(() => {
        if (isEdit) {
            const fetchSection = async () => {
                try {
                    const response = await SectionService.getSingle(+id!);
                    const section = response;

                    setSectionData({
                        title: section.title,
                        content: section.content
                    });
                } catch (error) {
                    ErrorToast(error);
                }
            };

            fetchSection();
        }
    }, [isEdit, id]);

    useEffect(() => {
        if (sectionData) {
            reset(sectionData);
        }
    }, [sectionData, reset]);

    const onSubmit = async (data: SectionState) => {
        try {
            isEdit
                ? await SectionService.update(data, +id!)
                : await SectionService.create(data);

            SuccessToast(t(`section.${isEdit ? 'successUpdate' : 'successCreate'}`));
            navigate('/sections');
            reset();
        } catch (error) {
            ErrorToast(error);
        }
    };

    return (
        <React.Fragment>
            <Row>
                <Col sm={12}>
                    <Card>
                        <CardBody>
                            <Form onSubmit={handleSubmit(onSubmit)}>
                                <FormErrorSummary errors={errors} />
                                <Row className="g-3">
                                    <Col sm={12} xl={6}>
                                        <div className="mb-0">
                                            <label className="form-label">{t("section.title")}</label>
                                            <Controller
                                                name="title"
                                                control={control}
                                                render={({ field }) => (
                                                    <input
                                                        type="text"
                                                        className={`form-control ${errors.title ? 'is-invalid' : ''}`}
                                                        placeholder={t("section.enterTitle")}
                                                        {...field}
                                                    />
                                                )}
                                            />
                                            {errors.title && <div className="invalid-feedback">
                                                {t(errors.title.message as string)}
                                            </div>}
                                        </div>
                                    </Col>
                                    <Col sm={12}>
                                        <div className="mb-0">
                                            <label className="form-label">{t("section.content")}</label>
                                            <div className={errors.content ? 'is-invalid' : ''}>
                                                <Controller
                                                    name="content"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Editor
                                                            apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
                                                            value={field.value}
                                                            onEditorChange={(content: string) => field.onChange(content)}
                                                            init={{
                                                                height: 360,
                                                                menubar: false,
                                                                branding: false,
                                                                plugins: [
                                                                    'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                                                                    'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                                                                    'insertdatetime', 'media', 'table', 'help', 'wordcount',
                                                                ],
                                                                toolbar:
                                                                    'undo redo | blocks | bold italic underline strikethrough | ' +
                                                                    'alignleft aligncenter alignright alignjustify | ' +
                                                                    'bullist numlist outdent indent | table | link image | removeformat | code',
                                                                content_style: 'body { font-family: Helvetica, Arial, sans-serif; font-size: 14px; }',
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </div>
                                            {errors.content && <div className="invalid-feedback">
                                                {t(errors.content.message as string)}
                                            </div>}
                                        </div>
                                    </Col>
                                    <Col sm={12}>
                                        <Button type="submit" variant="primary">
                                            {t(`section.${isEdit ? 'update' : 'create'}`)}
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </React.Fragment>
    );
};

export default SectionForm;